import { ChangeDetectorRef, Component, HostListener, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import {
  Alert,
  AlertService,
  AlertType,
  ModalComponent,
  ModalResult,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig
} from '@michelin/theme';
import { Pagination, WheelColor, WheelColorFiltersResponse } from '../../models/response.model';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';
import { ProductDataService } from '../../services/product-data.service';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { Store } from '@ngxs/store';
import { CanComponentDeactivate } from '../../../../shared/guards/unsaved-changes.guard';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { AuthenticationService } from '../../../../features/authentication/services/authentication.service';

@Component({
  selector: 'app-wheel-color',
  templateUrl: './wheel-color.component.html',
  styleUrls: ['./wheel-color.component.scss'],
  standalone: false
})
export class WheelColorComponent implements OnInit, CanComponentDeactivate {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<Element>;
  @ViewChild('deleteConfirmation', { static: true }) deleteModal: ModalComponent;
  @ViewChild('deleteContent') deleteContent: TemplateRef<any>;
  @ViewChild('discardConfirmation', { static: true }) discardModal: ModalComponent;
  @ViewChild('discardContent') discardContent: TemplateRef<any>;

  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public featureFlagsService = inject(FeatureflagsService);
  public alertService = inject(AlertService);
  public cdr = inject(ChangeDetectorRef);
  private readonly authService = inject(AuthenticationService);

  filterForm: FormGroup;
  filterFormBackend: FormGroup;
  wheelColorList: WheelColor[] = [];
  wheelColorCodeFilterList: string[] = [];
  wheelColorDescriptionFilterList: string[] = [];
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  isSaveEnabled = false;
  locallyAddedWheelColors: WheelColor[] = [];
  itemToDelete: WheelColor | null = null;
  productSaveDisabled = true;
  private controlMap = new Map<string, Map<string, FormControl>>();

  pagination: Pagination;
  currentPage = 1;
  pageSize = 10;
  pageSizeOptions = [5, 10, 50, 100];

  tableConfig: TableConfig<WheelColor> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    columns: {}
  };

  fetcher: PaginatorFetcher<WheelColor> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    this.generateFilterForm();

    return this.productDataService.getWheelColors({ ...this.filterFormBackend.value, ...filters }, filters.page, filters.limit).pipe(
      map((response: any) => {
        const { wheelColors = [], pagination } = response.data || {};
        this.wheelColorList = wheelColors;
        this.pagination = pagination;
        const combined = [...this.locallyAddedWheelColors, ...this.wheelColorList];
        this.updateDisplayedRowsForm(combined);
        const totalCount = (pagination?.totalRecords || wheelColors.length) + this.locallyAddedWheelColors.length;
        return {
          items: combined,
          count: totalCount
        };
      })
    );
  };

  constructor() {
    this.filterForm = this.fb.group({
      wheelColorCodes: [[]],
      wheelColorDescriptions: [[]]
    });
    this.filterFormBackend = this.fb.group({
      wheelColorCodes: [[]],
      wheelColorDescriptions: [[]]
    });
  }

  generateFilterForm(): void {
    this.filterFormBackend.patchValue({
      wheelColorCodes: this.filterForm.get('wheelColorCodes')?.value || '',
      wheelColorDescriptions: this.filterForm.get('wheelColorDescriptions')?.value || ''
    });
  }

  ngOnInit(): void {
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    if (this.productSaveDisabled) {
      this.alertService.showAlert({
        type: AlertType.classical,
        title: this.translationService.translate('readOnlyProductDataHeader'),
        content: this.translationService.translate('readOnlyProductDataText'),
        timestamp: 60000
      } as Alert);
    }
    this.setupFormListeners();
    this.loadWheelColorFilters();
    this.displayedRowsForm.valueChanges.subscribe(() => {
      this.checkIfModified();
    });
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.tableConfig.columns = {
        wheelColorCode: {
          cell: 'wheelColorCode',
          header: this.translationService.translate('wheelColorCodeText') || 'Wheel Color Code'
        },
        wheelColorDescription: {
          cell: 'wheelColorDescription',
          header: this.translationService.translate('wheelColorDescriptionText') || 'Wheel Color Description'
        },
        action: {
          cell: 'action',
          header: this.translationService.translate('actionText') || 'Action'
        }
      };
      this.cdr.markForCheck();
    });
  }

  private loadWheelColorFilters(): void {
    this.productDataService.getWheelColorFilters().subscribe((response: WheelColorFiltersResponse) => {
      this.wheelColorCodeFilterList = response.data.wheelColorCodes || [];
      this.wheelColorDescriptionFilterList = response.data.wheelColorDescriptions || [];
    });
  }

  resetFilters(): void {
    this.filterForm.reset();
  }

  private setupFormListeners(): void {
    this.filterForm.valueChanges.pipe(debounceTime(1000), distinctUntilChanged()).subscribe(() => {
      this.generateFilterForm();
      this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
    });
  }

  updateDisplayedRowsForm(items: WheelColor[]): void {
    // Snapshot current form values to preserve unsaved user edits
    const currentValues = new Map<string, any>();
    const existingFormArray = this.displayedRowsForm.get('rows') as FormArray;
    if (existingFormArray) {
      for (let i = 0; i < existingFormArray.length; i++) {
        const val = existingFormArray.at(i).value;
        if (val?.wheelColorPk) {
          currentValues.set(val.wheelColorPk, val);
        }
      }
    }

    this.controlMap.clear();
    const rowForms = items.map((row) => {
      // Use previously edited values if available (for unsaved local rows)
      const editedValues = currentValues.get(row.wheelColorPk);
      const rowData = editedValues ? { ...row, ...editedValues } : row;
      const group = this.createRowForm(rowData);
      const key = row.wheelColorPk || '';
      const fieldMap = new Map<string, FormControl>();
      fieldMap.set('wheelColorPk', group.get('wheelColorPk') as FormControl);
      fieldMap.set('wheelColorCode', group.get('wheelColorCode') as FormControl);
      fieldMap.set('wheelColorDescription', group.get('wheelColorDescription') as FormControl);
      this.controlMap.set(key, fieldMap);
      return group;
    });
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
  }

  createRowForm(row: WheelColor): FormGroup {
    return this.fb.group({
      wheelColorPk: [row.wheelColorPk],
      wheelColorCode: [row.wheelColorCode, [Validators.required]],
      wheelColorDescription: [row.wheelColorDescription, [Validators.required]]
    });
  }

  getControl(item: WheelColor, controlName: keyof WheelColor): FormControl {
    const key = item?.wheelColorPk || '';
    const fieldMap = this.controlMap.get(key);
    if (fieldMap) {
      const control = fieldMap.get(controlName as string);
      if (control) {
        return control;
      }
    }

    // Fallback to index-based lookup
    const combinedItems = [...this.locallyAddedWheelColors, ...this.wheelColorList];
    const index = combinedItems.findIndex((color) => color.wheelColorPk === item.wheelColorPk);

    if (index === -1) {
      return new FormControl('');
    }

    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    if (!formArray || formArray.length <= index) {
      return new FormControl('');
    }

    const formGroup = formArray.at(index);
    if (!formGroup) {
      return new FormControl('');
    }

    const control = formGroup.get(controlName as string) as FormControl;
    return control || new FormControl('');
  }

  get canEditWheelColor(): boolean {
    const roles = this.authService.getRolesFromToken();
    return roles.includes('SETUP_SERVICES_EDIT') && !this.productSaveDisabled;
  }

  checkIfModified(): void {
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    const combinedItems = [...this.locallyAddedWheelColors, ...this.wheelColorList];
    let modified = false;

    for (let i = 0; i < combinedItems.length; i++) {
      const original = combinedItems[i];
      const current = formArray.at(i)?.value;
      if (!current) continue;
      const hasData = current.wheelColorCode || current.wheelColorDescription;
      if (hasData && Object.keys(current).some((key) => current[key] !== original[key])) {
        modified = true;
        break;
      }
    }
    this.isSaveEnabled = modified && this.canEditWheelColor && !this.productSaveDisabled;
  }

  private validateWheelColorCode(current: any, index: number, allCurrentValues: any[]): { isValid: boolean; errorMessage?: string } {
    if (!current.wheelColorCode) {
      return {
        isValid: false,
        errorMessage: this.translationService.translate('wheelColorCodeRequiredText') || 'Wheel Color Code is required.'
      };
    }

    const duplicateCode = allCurrentValues.find(
      (item) => item.index !== index && item.wheelColorCode && item.wheelColorCode.toLowerCase() === current.wheelColorCode.toLowerCase()
    );
    if (duplicateCode) {
      return {
        isValid: false,
        errorMessage: this.translationService.translate('duplicateWheelColorCodeText') || 'Wheel Color Code must be unique.'
      };
    }

    return { isValid: true };
  }

  private validateWheelColorDescription(current: any, index: number, allCurrentValues: any[]): { isValid: boolean; errorMessage?: string } {
    if (!current.wheelColorDescription) {
      return {
        isValid: false,
        errorMessage: this.translationService.translate('wheelColorDescriptionRequiredText') || 'Wheel Color Description is required.'
      };
    }

    const duplicateDescription = allCurrentValues.find(
      (item) =>
        item.index !== index &&
        item.wheelColorDescription &&
        item.wheelColorDescription.toLowerCase() === current.wheelColorDescription.toLowerCase()
    );
    if (duplicateDescription) {
      return {
        isValid: false,
        errorMessage: this.translationService.translate('duplicateWheelColorDescriptionText') || 'Wheel Color Description must be unique.'
      };
    }

    return { isValid: true };
  }

  save(): void {
    if (!this.canEditWheelColor || !this.isSaveEnabled) {
      return;
    }

    const modifiedRows: WheelColor[] = [];
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    const allFormValues = formArray.value.map((val: any, idx: number) => ({ ...val, index: idx }));

    for (let i = 0; i < formArray.length; i++) {
      const current = formArray.at(i).value;

      if (!current.wheelColorCode && !current.wheelColorDescription) {
        continue;
      }

      const isValidCode = this.validateWheelColorCode(current, i, allFormValues);

      if (!isValidCode.isValid) {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: isValidCode.errorMessage!
        } as Alert);
        return;
      }

      const isValidDescription = this.validateWheelColorDescription(current, i, allFormValues);

      if (!isValidDescription.isValid) {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: isValidDescription.errorMessage!
        } as Alert);
        return;
      }
    }

    const totalRows = [...this.locallyAddedWheelColors, ...this.wheelColorList];
    for (let i = 0; i < totalRows.length; i++) {
      const original = totalRows[i];
      const current = formArray.at(i).value;

      const isModified = Object.keys(current).some((key) => current[key] !== original[key]);
      if (isModified) {
        modifiedRows.push({ ...original, ...current });
      }
    }

    if (modifiedRows.length > 0) {
      this.productDataService.updateWheelColors(modifiedRows).subscribe({
        next: (response: any) => {
          const responseData = response?.data || response;
          if (Array.isArray(responseData)) {
            responseData.forEach((updatedColor: WheelColor) => {
              const existingIndex = this.wheelColorList.findIndex((color) => color.wheelColorPk === updatedColor.wheelColorPk);
              if (existingIndex !== -1) {
                this.wheelColorList[existingIndex] = updatedColor;
              } else {
                this.wheelColorList.push(updatedColor);
              }
            });
          }

          this.alertService.showAlert({
            type: AlertType.success,
            content: this.translationService.translate('wheelColorSavedText') || 'Wheel Colors saved successfully'
          } as Alert);

          this.locallyAddedWheelColors = [];
          this.loadWheelColorFilters();
          this.tableRef.resetFilters();
          this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
          this.checkIfModified();
        },
        error: () => {
          this.alertService.showAlert({
            type: AlertType.critical,
            content: this.translationService.translate('wheelColorSaveErrorText') || 'Error encountered while saving Wheel Colors'
          } as Alert);
        }
      });
    }
  }

  addRow(): void {
    this.productDataService.getNewWheelColorPk().subscribe({
      next: (response: any) => {
        const newId = response?.data;
        const newRow: WheelColor = {
          wheelColorPk: newId,
          wheelColorCode: '',
          wheelColorDescription: ''
        };
        this.locallyAddedWheelColors.push(newRow);
        this.isSaveEnabled = true;
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
      },
      error: () => {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('criticalErrorAlertText')
        } as Alert);
      }
    });
  }

  deleteRow(item: WheelColor): void {
    if (this.productSaveDisabled) {
      return;
    }
    const locallyAddedIndex = this.locallyAddedWheelColors.findIndex((color) => color.wheelColorPk === item.wheelColorPk);

    if (locallyAddedIndex !== -1) {
      this.locallyAddedWheelColors.splice(locallyAddedIndex, 1);

      this.alertService.showAlert({
        type: AlertType.success,
        content: this.translationService.translate('wheelColorDeleteText') || 'Wheel Color Deleted Successfully'
      } as Alert);

      this.updateDisplayedRowsForm([...this.locallyAddedWheelColors, ...this.wheelColorList]);
      this.tableRef.resetFilters();
      this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
      this.checkIfModified();

      return;
    }
    const payload = { ...item }.wheelColorPk;
    this.productDataService.deleteWheelColor(payload).subscribe({
      next: () => {
        this.alertService.showAlert({
          type: AlertType.success,
          content: this.translationService.translate('wheelColorDeleteText') || 'Wheel Color Deleted Successfully'
        } as Alert);
        this.wheelColorList = this.wheelColorList.filter((row) => row.wheelColorPk !== item.wheelColorPk);
        this.updateDisplayedRowsForm([...this.locallyAddedWheelColors, ...this.wheelColorList]);
        this.loadWheelColorFilters();
        this.resetFilters();
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
        this.isSaveEnabled = false;
      },
      error: () => {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('wheelColorDeleteErrorText') || 'Error encountered while deleting the Wheel Color'
        } as Alert);
      }
    });
  }

  showDeleteModal(item: WheelColor): void {
    this.deleteModal.open({
      id: 'deleteConfirmation',
      title: this.translationService.translate('warningText'),
      width: 550,
      content: this.deleteContent,
      confirm: this.translationService.translate('yesText'),
      cancel: this.translationService.translate('noText')
    });
    this.itemToDelete = item;
  }

  onDeleteModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'deleteConfirmation' && result.result) {
        this.deleteRow(this.itemToDelete);
      } else {
        this.itemToDelete = null;
      }
    }
  }

  showDiscardModal(): void {
    this.discardModal.open({
      id: 'discardConfirmation',
      title: this.translationService.translate('discardChangesText'),
      width: 550,
      content: this.discardContent,
      confirm: this.translationService.translate('okText'),
      cancel: this.translationService.translate('cancelText')
    });
  }

  onDiscardModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'discardConfirmation' && result.result) {
        this.locallyAddedWheelColors = [];
        this.updateDisplayedRowsForm(this.wheelColorList);
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
        this.isSaveEnabled = false;
      }
    }
  }

  canDeactivate(): Promise<boolean> {
    if (this.isSaveEnabled) {
      return new Promise<boolean>((resolve) => {
        this.showDiscardModal();
        const modalSubscription = this.discardModal.result.subscribe((result: ModalResult) => {
          if (result && result.id === 'discardConfirmation') {
            resolve(!!result.result);
            modalSubscription.unsubscribe();
          }
        });
      });
    }
    return Promise.resolve(true);
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.isSaveEnabled) {
      $event.returnValue = this.translationService.translate('discardChangesText');
    }
  }
}
